import { useState, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useEffectivePermissions } from '../../auth/useEffectivePermissions'
import { BargeldlosDialog } from '../kasse/components/BargeldlosDialog'
import { SchultypDialog } from './SchultypDialog'
import { schultypVon } from './schultyp'
import { useSchultypOhne } from './api/schultypApi'
import { useSchulen, useCreateSchool, useUpdateSchool, useDeleteSchool, type School, type SchoolInput } from './api/schulenApi'
import { useHierarchie } from './hierarchie/api/hierarchieApi'
import SchoolEditorModal from './SchoolEditorModal'
import PcaSyncDialog from './PcaSyncDialog'
import { notify } from '../../store/notificationStore'
import { ApiError } from '../../shared/api'
import { PageHeader, Badge, Segmented, Button } from '../../shared/ui'

// Extract a human-readable error message from any thrown error. Backend
// responses come as `{"error": "..."}` JSON; we unwrap so the toast actually
// tells the user what went wrong instead of a generic "fehlgeschlagen".
function describeError(e: unknown, fallback: string): string {
  if (e instanceof ApiError) {
    try {
      const parsed = JSON.parse(e.message)
      if (parsed && typeof parsed.error === 'string') return `${fallback}: ${parsed.error}`
    } catch {
      // body wasn't JSON — fall through to raw message
    }
    if (e.message && e.message.length < 200) return `${fallback}: ${e.message}`
  }
  return fallback
}

/** School type of a raw value for cards, rows and the group counts. Unknown or empty text is "Ohne Schultyp", never shown as its own type. */
const schultypLabel = (raw: string | null | undefined): string => schultypVon(raw) ?? 'Ohne Schultyp'

// ── Components ───────────────────────────────────────────────────────────────

const SchoolCard = ({ school, onClick }: { school: School, onClick: () => void }) => {
  const isActive = school.status?.toLowerCase() === 'aktiv'
  
  return (
    <div
      onClick={onClick}
      className="group bg-surface-container-lowest border border-outline-variant/40 rounded-[2rem] p-6 shadow-sm hover:shadow-2xl hover:border-primary/20 transition-all duration-500 cursor-pointer overflow-hidden relative flex flex-col"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
      <div className="flex items-start justify-between mb-4">
         <div className="w-14 h-14 rounded-2xl bg-surface-container-low flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-all shadow-inner">
            <span className="material-symbols-outlined text-outline-variant group-hover:text-primary transition-colors text-2xl">apartment</span>
         </div>
         <div className="flex flex-col items-end gap-1">
           <Badge tone={isActive ? 'success' : 'warn'}>{school.status || 'Unbekannt'}</Badge>
           <span className="text-[9px] font-black text-outline-variant uppercase tracking-widest group-hover:text-primary">{school.schulnummer || '—'}</span>
         </div>
      </div>
      <h3 className="font-bold text-lg text-on-surface group-hover:text-primary transition-colors mb-1 min-h-[3.5em] leading-snug">
        {school.name}
      </h3>
      {school.abkuerzung && (
        <p className="text-[10px] font-black uppercase tracking-widest text-primary/60 mb-2">{school.abkuerzung}</p>
      )}
      <p className="text-xs font-medium text-on-surface-variant mb-6 flex-1">{school.stadt} · {school.bundesland || '—'}</p>
      <div className="grid grid-cols-2 gap-2 mt-auto">
         <div className="px-3 py-2 bg-surface-container-low rounded-xl">
            <p className="text-[8px] font-black uppercase tracking-widest text-on-surface-variant mb-0.5">Leiter</p>
            <p className="text-[11px] font-bold text-on-surface truncate">{school.projektleiter || '—'}</p>
         </div>
         <div className="px-3 py-2 bg-surface-container-low rounded-xl">
            <p className="text-[8px] font-black uppercase tracking-widest text-on-surface-variant mb-0.5">Typ</p>
            <p className="text-[11px] font-bold text-on-surface">{schultypLabel(school.kinderkategorie)}</p>
         </div>
      </div>
    </div>
  )
}

const SchoolRow = ({ school, onClick }: { school: School, onClick: () => void }) => {
  const isActive = school.status?.toLowerCase() === 'aktiv'
  
  return (
    <tr onClick={onClick} className="hover:bg-surface-container-low transition-colors cursor-pointer border-b border-outline-variant/15">
      <td className="px-6 py-4 font-mono text-[10px] text-on-surface-variant uppercase tracking-widest whitespace-nowrap">{school.schulenId || '—'}</td>
      <td className="px-6 py-4">
        <div className="font-bold text-on-surface text-sm">{school.name}</div>
        {school.abkuerzung && <div className="text-[10px] text-primary font-bold uppercase tracking-widest mt-1">{school.abkuerzung}</div>}
      </td>
      <td className="px-6 py-4">
        <Badge tone={isActive ? 'success' : 'warn'}>{school.status || '—'}</Badge>
      </td>
      <td className="px-6 py-4 text-xs text-on-surface-variant">{school.stadt}</td>
      <td className="px-6 py-4 text-xs text-on-surface-variant">{school.projektleiter || '—'}</td>
      <td className="px-6 py-4 text-xs text-on-surface-variant">
        <span className="px-2 py-1 rounded-md bg-surface-container font-bold text-[9px] uppercase tracking-wider whitespace-nowrap">{schultypLabel(school.kinderkategorie)}</span>
      </td>
    </tr>
  )
}

// ── Main Dashboard ────────────────────────────────────────────────────────────

export default function SchulenDashboard() {
  const { t } = useTranslation('kasse')
  const { t: ts } = useTranslation('schulen')
  const { canWrite } = useEffectivePermissions()
  const canManageBargeldlos = canWrite('schulen')
  const [bargeldlosOpen, setBargeldlosOpen] = useState(false)
  const [schultypOpen, setSchultypOpen] = useState(false)
  // M27 T10b: how many schools (active AND inactive, owner t10b_inaktiv b) still lack a valid school type. One row is enough, only `gesamt` is used. A failed read
  // (route or migration 377 missing) leaves the count undefined and hides the button.
  const schultypOhne = useSchultypOhne({ proSeite: 1 }, canManageBargeldlos)
  const schultypOhneAnzahl = schultypOhne.data?.gesamt ?? 0
  const { data: schools = [] } = useSchulen()
  const { data: hierarchie = [] } = useHierarchie()
  const createMutation = useCreateSchool()
  const updateMutation = useUpdateSchool()
  const deleteMutation = useDeleteSchool()

  const [search, setSearch] = useState('')
  const [view, setView] = useState<'grid' | 'list'>('grid')
  const [statusFilter, setStatusFilter] = useState<'Alle' | 'Aktiv' | 'Inaktiv'>('Aktiv')
  const [selectedSchool, setSelectedSchool] = useState<School | null>(null)
  const [modalMode, setModalMode] = useState<'view' | 'edit' | 'add' | null>(null)
  const [pcaSyncOpen, setPcaSyncOpen] = useState(false)
  const openView = (school: School) => { setSelectedSchool(school); setModalMode('view') }
  const openAdd  = () => { setSelectedSchool(null); setModalMode('add') }
  const closeModal = () => { setSelectedSchool(null); setModalMode(null) }

  const filtered = useMemo(() => schools.filter((s) => {
    const matchesSearch = !search || s.name.toLowerCase().includes(search.toLowerCase()) || (s.stadt ?? '').toLowerCase().includes(search.toLowerCase()) || (s.abkuerzung ?? '').toLowerCase().includes(search.toLowerCase())
    
    if (!matchesSearch) return false;
    
    const sStatus = (s.status || '').toLowerCase()
    if (statusFilter === 'Aktiv') return sStatus === 'aktiv'
    if (statusFilter === 'Inaktiv') return sStatus !== 'aktiv'
    return true
  }), [schools, search, statusFilter])

  const kpis = useMemo(() => {
    let active = 0
    let inactive = 0
    const categories: Record<string, number> = {}

    schools.forEach(s => {
      if ((s.status || '').toLowerCase() === 'aktiv') active++
      else inactive++

      const cat = schultypLabel(s.kinderkategorie)
      categories[cat] = (categories[cat] || 0) + 1
    })

    // Projektleiter — derive from authoritative /api/hierarchie (ops.SchuleStaff)
    const plCounts: Record<string, number> = {}
    let plSchoolsAssigned = 0
    hierarchie.forEach(g => {
      const totalSchoolsInGroup = 1 + g.standorte.length
      const plName = g.hauptschule.projektleiter?.name
      if (plName) {
        plCounts[plName] = (plCounts[plName] || 0) + totalSchoolsInGroup
        plSchoolsAssigned += totalSchoolsInGroup
      }
    })
    const plTop = Object.entries(plCounts).sort((a, b) => b[1] - a[1])
    const plTotal = plTop.length
    const plAvg = plTotal > 0 ? plSchoolsAssigned / plTotal : 0

    // Standortleitungen — across all Standorte. Kita / Hort are usually delivery-only
    // (kein Personal vor Ort), so they are NOT counted as "offen" when no SL is set.
    const DELIVERY_ONLY_CATS = new Set(['kita', 'hort'])
    const isDeliveryOnly = (cat: string | null) => cat != null && DELIVERY_ONLY_CATS.has(cat.toLowerCase())

    let slGesamt = 0        // Standorte with a Standortleiter assigned
    let slMitVertretung = 0 // Standorte that also have at least one Ersatz
    let slOffen = 0         // Standorte that need an SL but have none
    let slLieferung = 0     // Standorte that don't need an SL (Kita / Hort)
    hierarchie.forEach(g => {
      for (const s of g.standorte) {
        if (s.standortleiter) {
          slGesamt++
          if (s.ersatz.length > 0) slMitVertretung++
        } else if (isDeliveryOnly(s.kategorie)) {
          slLieferung++
        } else {
          slOffen++
        }
      }
    })

    return { active, inactive, categories, plTop, plTotal, plAvg, slGesamt, slMitVertretung, slOffen, slLieferung }
  }, [schools, hierarchie])

  const handleSave = async (data: SchoolInput) => {
    try {
      if (modalMode === 'add') {
        await createMutation.mutateAsync(data)
        notify.success('Schule erfolgreich angelegt')
      } else if (selectedSchool?.id) {
        await updateMutation.mutateAsync({ id: selectedSchool.id, data })
        notify.success('Schule erfolgreich gespeichert')
      }
      closeModal()
    } catch (e) {
      console.error('[SchulenDashboard] handleSave failed:', e, '\npayload:', data)
      notify.error(describeError(e, 'Speichern fehlgeschlagen'))
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id)
      notify.success('Schule gelöscht')
      closeModal()
    } catch (e) {
      console.error('[SchulenDashboard] handleDelete failed:', e)
      notify.error(describeError(e, 'Löschen fehlgeschlagen'))
    }
  }

  return (
    <>
      <div className="w-full pb-20 animate-[fade-in_0.5s_ease-out]">
        
        {/* KPI Section */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="liquid-glass rounded-[1.5rem] p-5 flex flex-col justify-between">
            <span className="text-[10px] font-black uppercase tracking-widest text-on-surface-variant">Gesamt Standorte</span>
            <div className="text-3xl font-bold text-on-surface mt-2">{schools.length}</div>
          </div>
          <div className="bg-tertiary-container/30 rounded-[1.5rem] p-5 border border-tertiary-container/40 shadow-sm flex flex-col justify-between">
            <span className="text-[10px] font-black uppercase tracking-widest text-on-tertiary-container">Aktive Standorte</span>
            <div className="text-3xl font-bold text-on-tertiary-container mt-2">{kpis.active}</div>
          </div>
          <div className="bg-surface-container-low rounded-[1.5rem] p-5 border border-outline-variant/40 shadow-sm flex flex-col justify-between">
            <span className="text-[10px] font-black uppercase tracking-widest text-on-surface-variant">Inaktive Standorte</span>
            <div className="text-3xl font-bold text-slate-600 mt-2">{kpis.inactive}</div>
          </div>
          <div className="liquid-glass rounded-[1.5rem] p-5 overflow-hidden">
            <span className="text-[10px] font-black uppercase tracking-widest text-on-surface-variant block mb-2">Nach Schultyp</span>
            <div className="flex flex-wrap gap-2 max-h-[60px] overflow-y-auto pr-2 custom-scrollbar">
              {Object.entries(kpis.categories).map(([cat, count]) => (
                <div key={cat} className="flex items-center gap-1.5 bg-surface-container px-2 py-1 rounded-lg">
                  <span className="text-[9px] font-bold text-on-surface-variant">{cat}</span>
                  <span className="text-[9px] font-black text-primary bg-primary/10 px-1.5 rounded">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Personal Sections — clickable cards to detail pages */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">

          {/* Projektleiter — Zuweisung in der Hierarchie */}
          <Link
            to="/schulen/hierarchie"
            className="group block liquid-glass rounded-[1.5rem] p-6 hover:-translate-y-0.5 transition-transform duration-300"
          >
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <span className="material-symbols-outlined text-primary !text-xl">supervisor_account</span>
                </div>
                <div>
                  <span className="text-[10px] font-black uppercase tracking-widest text-on-surface-variant block">Projektleiter</span>
                  <span className="text-sm font-bold text-on-surface">Zuständigkeiten</span>
                </div>
              </div>
              <span className="material-symbols-outlined text-on-surface-variant group-hover:text-primary group-hover:translate-x-1 transition-all !text-lg">arrow_forward</span>
            </div>

            <div className="flex gap-6 mb-4">
              <div>
                <p className="text-[9px] font-black uppercase tracking-widest text-on-surface-variant">Gesamt</p>
                <p className="text-2xl font-bold text-on-surface mt-1">{kpis.plTotal}</p>
              </div>
              <div>
                <p className="text-[9px] font-black uppercase tracking-widest text-on-surface-variant">Ø Schulen</p>
                <p className="text-2xl font-bold text-on-surface mt-1">{kpis.plAvg.toFixed(1)}</p>
              </div>
            </div>

            {kpis.plTop.length > 0 ? (
              <div className="grid grid-cols-2 gap-2">
                {kpis.plTop.slice(0, 4).map(([name, count]) => (
                  <div key={name} className="flex items-center justify-between gap-2 px-3 py-2 bg-surface-container-low rounded-xl group-hover:bg-primary/5 transition-colors min-w-0">
                    <span className="text-xs font-bold text-on-surface truncate">{name}</span>
                    <span className="text-[10px] font-black text-primary bg-primary/10 px-2 py-0.5 rounded-full flex-shrink-0">{count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-on-surface-variant italic">Keine Projektleiter zugewiesen</p>
            )}
          </Link>

          {/* Standortleitungen — Zuweisung in der Hierarchie */}
          <Link
            to="/schulen/hierarchie"
            className="group block liquid-glass rounded-[1.5rem] p-6 hover:-translate-y-0.5 transition-transform duration-300"
          >
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <span className="material-symbols-outlined text-primary !text-xl">manage_accounts</span>
                </div>
                <div>
                  <span className="text-[10px] font-black uppercase tracking-widest text-on-surface-variant block">Standortleitungen</span>
                  <span className="text-sm font-bold text-on-surface">Personal pro Standort</span>
                </div>
              </div>
              <span className="material-symbols-outlined text-on-surface-variant group-hover:text-primary group-hover:translate-x-1 transition-all !text-lg">arrow_forward</span>
            </div>

            <div className="flex flex-wrap gap-x-6 gap-y-3 mb-4">
              <div>
                <p className="text-[9px] font-black uppercase tracking-widest text-on-surface-variant">Gesamt</p>
                <p className="text-2xl font-bold text-on-surface mt-1">{kpis.slGesamt}</p>
              </div>
              <div>
                <p className="text-[9px] font-black uppercase tracking-widest text-on-surface-variant">Mit Vertretung</p>
                <p className="text-2xl font-bold text-on-surface mt-1">{kpis.slMitVertretung}</p>
              </div>
              <div>
                <p className="text-[9px] font-black uppercase tracking-widest text-on-surface-variant">Offen</p>
                <p className={`text-2xl font-bold mt-1 ${kpis.slOffen > 0 ? 'text-amber-600' : 'text-on-surface'}`}>{kpis.slOffen}</p>
              </div>
              {kpis.slLieferung > 0 && (
                <div title="Kita & Hort — nur Essenslieferung, kein Personal vor Ort">
                  <p className="text-[9px] font-black uppercase tracking-widest text-on-surface-variant">Nur Lieferung</p>
                  <p className="text-2xl font-bold text-on-surface-variant mt-1">{kpis.slLieferung}</p>
                </div>
              )}
            </div>

            {kpis.slOffen > 0 ? (
              <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-100 rounded-xl">
                <span className="material-symbols-outlined text-amber-500 !text-base">priority_high</span>
                <p className="text-[11px] font-medium text-amber-700">
                  {kpis.slOffen} {kpis.slOffen === 1 ? 'Standort ohne' : 'Standorte ohne'} Standortleitung — in der Hierarchie zuweisen
                </p>
              </div>
            ) : (
              <div className="flex items-center gap-2 px-3 py-2 bg-tertiary-container/30 border border-tertiary-container/40 rounded-xl">
                <span className="material-symbols-outlined text-on-tertiary-container !text-base">check_circle</span>
                <p className="text-[11px] font-medium text-on-tertiary-container">Alle personalbesetzten Standorte haben eine Standortleitung</p>
              </div>
            )}
          </Link>

          {/* Kalender-Kontrolle — Feiertag ⇒ Sperrtag Verstöße + Ebenen-Überschneidungen + Zeit-Konflikte */}
          <Link
            to="/schulen/sperrtag-kontrolle"
            className="group block liquid-glass rounded-[1.5rem] p-6 hover:-translate-y-0.5 transition-transform duration-300"
          >
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <span className="material-symbols-outlined text-primary !text-xl">calendar_month</span>
                </div>
                <div>
                  <span className="text-[10px] font-black uppercase tracking-widest text-on-surface-variant block">Kalender-Kontrolle</span>
                  <span className="text-sm font-bold text-on-surface">Sperrtage, Ferien & Zeit-Konflikte</span>
                </div>
              </div>
              <span className="material-symbols-outlined text-on-surface-variant group-hover:text-primary group-hover:translate-x-1 transition-all !text-lg">arrow_forward</span>
            </div>
            <p className="text-xs text-on-surface-variant">
              Prüft je Schule, ob jeder werktägliche Feiertag mit einem PCA-Sperrtag abgedeckt ist, wie vollständig die Ferien mit Sperrtagen belegt sind, und wo Personalzeiten mit Feiertag oder Krankmeldung kollidieren.
            </p>
          </Link>

        </div>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
          <PageHeader
            title={`${filtered.length} Schulstandorte`}
            subtitle={`von ${schools.length} gesamt`}
          />

          <div className="flex flex-wrap items-center gap-3">
            {canManageBargeldlos && (
              <Button icon="credit_card_off" onClick={() => setBargeldlosOpen(true)}>{t('bargeldlos.title')}</Button>
            )}
            {canManageBargeldlos && schultypOhneAnzahl > 0 && (
              <Button icon="category" onClick={() => setSchultypOpen(true)} aria-label={ts('schultyp.openCount', { count: schultypOhneAnzahl })}>
                {ts('schultyp.open')}
                <Badge tone="warn" size="sm">{schultypOhneAnzahl}</Badge>
              </Button>
            )}
            {/* Status Filter */}
            <Segmented<'Alle' | 'Aktiv' | 'Inaktiv'>
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: 'Alle',    label: 'Alle' },
                { value: 'Aktiv',   label: 'Aktiv' },
                { value: 'Inaktiv', label: 'Inaktiv' },
              ]}
            />

            <Segmented<'grid' | 'list'>
              value={view}
              onChange={setView}
              size="sm"
              options={[
                { value: 'grid', icon: 'grid_view', title: 'Kacheln' },
                { value: 'list', icon: 'list',      title: 'Liste'   },
              ]}
            />
            
            <input 
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Suche..." 
              className="bg-surface-container-lowest border border-slate-200 rounded-xl px-4 py-2.5 text-xs font-medium outline-none focus:border-primary w-48"
            />
            
            <button onClick={() => setPcaSyncOpen(true)} className="px-4 py-2.5 bg-surface-container-low border border-outline-variant text-on-surface rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-surface-container transition-colors" title="PCA-Standorte mit Schulen abgleichen (fm.Schools + dim.School)">PCA-Abgleich</button>
            <button onClick={openAdd} className="px-6 py-2.5 bg-primary text-white rounded-xl font-bold text-xs uppercase tracking-widest hover:scale-105 transition-transform">Hinzufügen</button>
          </div>
        </div>

        {view === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
            {filtered.map(s => <SchoolCard key={s.id} school={s} onClick={() => openView(s)} />)}
          </div>
        ) : (
          <div className="bg-surface-container-lowest border border-outline-variant/40 rounded-3xl overflow-x-auto shadow-sm">
            <table className="w-full min-w-[44rem] text-left">
              <thead>
                <tr className="text-[10px] font-bold text-on-surface-variant uppercase tracking-widest border-b border-slate-100 bg-surface-container-low">
                  <th className="px-6 py-4">ID</th>
                  <th className="px-6 py-4">Name</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4">Stadt</th>
                  <th className="px-6 py-4">Projektleiter</th>
                  <th className="px-6 py-4">Schultyp</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filtered.map(s => <SchoolRow key={s.id} school={s} onClick={() => openView(s)} />)}
              </tbody>
            </table>
          </div>
        )}
      </div>


      {modalMode && createPortal(
        <SchoolEditorModal
          school={selectedSchool}
          initialMode={modalMode}
          onClose={closeModal}
          onSave={handleSave}
          onDelete={selectedSchool?.id ? handleDelete : undefined}
          isSaving={createMutation.isPending || updateMutation.isPending}
          isDeleting={deleteMutation.isPending}
        />,
        document.body
      )}
      <PcaSyncDialog open={pcaSyncOpen} onClose={() => setPcaSyncOpen(false)} />
      {canManageBargeldlos && <BargeldlosDialog open={bargeldlosOpen} onClose={() => setBargeldlosOpen(false)} />}
      {canManageBargeldlos && <SchultypDialog open={schultypOpen} onClose={() => setSchultypOpen(false)} />}
    </>
  )
}
